import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../Global/Layout';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { API_CONFIG, LOTTO_TYPE, generatePromptPayQRCodeURL, PAYMENT_TIME_MINUTES, parseDatetimeLocal } from '../../../contexts/constants';
import { formatDateFLot, formatDateTimeTable } from '../../../utils/dateFormatter';
import { showConfirm, showError, showWarning, showSuccess } from '../../../utils/sweetAlertConfig';

export const normalizeActiveFlag = (value) => value === true || value === 1 || value === '1';

export const resolveDefaultActiveTab = (isPowerballActive, isMegaMillionsActive) => {
	if (isPowerballActive) return 'pb';
	if (isMegaMillionsActive) return 'mg';
	return null;
};

export const resolveActiveTab = (currentTab, isPowerballActive, isMegaMillionsActive) => {
	if (currentTab === 'pb' && isPowerballActive) return 'pb';
	if (currentTab === 'mg' && isMegaMillionsActive) return 'mg';
	return resolveDefaultActiveTab(isPowerballActive, isMegaMillionsActive) || currentTab;
};

export const resetPurchaseState = ({
	setPowerballTickets,
	setMegaMillionsTickets,
	setPaymentMethod,
	setSlipFile,
	setSlipPreview,
	setCreatedOrderId,
	setActiveTab,
	setUseDiscountPB,
	setUseDiscountMG,
	setUseDiscountPBOld,
	setUseDiscountMGOld,
	isPowerballActive,
	isMegaMillionsActive
}) => {
	setPowerballTickets([]);
	setMegaMillionsTickets([]);
	setPaymentMethod('scan');
	setSlipFile(null);
	setSlipPreview(null);
	setCreatedOrderId(null);
	if (setUseDiscountPB) setUseDiscountPB(false);
	if (setUseDiscountMG) setUseDiscountMG(false);
	if (setUseDiscountPBOld) setUseDiscountPBOld(false);
	if (setUseDiscountMGOld) setUseDiscountMGOld(false);
	if (setActiveTab) {
		const nextTab = resolveDefaultActiveTab(isPowerballActive, isMegaMillionsActive);
		if (nextTab) {
			setActiveTab(nextTab);
		}
	}
};

const getNumberValue = (value) => Number(value) || 0;

export const calculateDiscountInfo = ({
	validPowerballTickets = [],
	validMegaMillionsTickets = [],
	useDiscountPB = false,
	useDiscountMG = false,
	useDiscountPBOld = false,
	useDiscountMGOld = false,
	pricePerTicketPB = 0,
	pricePerTicketMG = 0,
	saleConfig = {}
}) => {
	let pbFreeTickets = 0;
	let mgFreeTickets = 0;
	let pbDiscountAmount = 0;
	let mgDiscountAmount = 0;

	const customerPbNewCount = getNumberValue(saleConfig.customer_pb_new_count);
	const customerPbNewFree = getNumberValue(saleConfig.customer_pb_new_free);
	const customerPbOldCount = getNumberValue(saleConfig.customer_pb_old_count);
	const customerPbOldFree = getNumberValue(saleConfig.customer_pb_old_free);
	const discountPbNewCustomerStatus = getNumberValue(saleConfig.discount_pb_new_customer_status);
	const discountPbOrderNumber = getNumberValue(saleConfig.discount_pb_order_number);
	const customerMgNewCount = getNumberValue(saleConfig.customer_mg_new_count);
	const customerMgNewFree = getNumberValue(saleConfig.customer_mg_new_free);
	const customerMgOldCount = getNumberValue(saleConfig.customer_mg_old_count);
	const customerMgOldFree = getNumberValue(saleConfig.customer_mg_old_free);
	const discountMgNewCustomerStatus = getNumberValue(saleConfig.discount_mg_new_customer_status);
	const discountMgOrderNumber = getNumberValue(saleConfig.discount_mg_order_number);

	const minPbTickets = customerPbNewCount + customerPbNewFree;
	if (useDiscountPB && discountPbNewCustomerStatus === 0 && minPbTickets > 0 && validPowerballTickets.length >= minPbTickets) {
		pbFreeTickets = Math.floor(validPowerballTickets.length / minPbTickets) * customerPbNewFree;
		pbDiscountAmount = pbFreeTickets * getNumberValue(pricePerTicketPB);
	}

	if (useDiscountPBOld && discountPbNewCustomerStatus === 1 && customerPbOldCount > 0) {
		const minPbOldTickets = customerPbOldCount + customerPbOldFree;
		const totalPBTickets = discountPbOrderNumber + validPowerballTickets.length;
		const setsEarned = minPbOldTickets > 0 ? Math.min(1, Math.floor(totalPBTickets / minPbOldTickets)) : 0;

		if (setsEarned > 0 && totalPBTickets >= minPbOldTickets) {
			pbFreeTickets = customerPbOldFree * setsEarned;
			pbDiscountAmount = pbFreeTickets * getNumberValue(pricePerTicketPB);
		}
	}

	const minMgTickets = customerMgNewCount + customerMgNewFree;
	if (useDiscountMG && discountMgNewCustomerStatus === 0 && minMgTickets > 0 && validMegaMillionsTickets.length >= minMgTickets) {
		mgFreeTickets = Math.floor(validMegaMillionsTickets.length / minMgTickets) * customerMgNewFree;
		mgDiscountAmount = mgFreeTickets * getNumberValue(pricePerTicketMG);
	}

	if (useDiscountMGOld && discountMgNewCustomerStatus === 1 && customerMgOldCount > 0) {
		const minMgOldTickets = customerMgOldCount + customerMgOldFree;
		const totalMGTickets = discountMgOrderNumber + validMegaMillionsTickets.length;
		const setsEarned = minMgOldTickets > 0 ? Math.min(1, Math.floor(totalMGTickets / minMgOldTickets)) : 0;

		if (setsEarned > 0 && totalMGTickets >= minMgOldTickets) {
			mgFreeTickets = customerMgOldFree * setsEarned;
			mgDiscountAmount = mgFreeTickets * getNumberValue(pricePerTicketMG);
		}
	}

	return {
		pbFreeTickets,
		mgFreeTickets,
		pbDiscountAmount,
		mgDiscountAmount,
		totalDiscountAmount: pbDiscountAmount + mgDiscountAmount
	};
};

export const calculateTotalPriceAllTickets = ({
	validPowerballTickets = [],
	validMegaMillionsTickets = [],
	pricePerTicketPB = 0,
	pricePerTicketMG = 0,
	discountInfo = {}
}) => (
	(validPowerballTickets.length * getNumberValue(pricePerTicketPB)) +
	(validMegaMillionsTickets.length * getNumberValue(pricePerTicketMG)) -
	getNumberValue(discountInfo.totalDiscountAmount)
);

export const calculateOldCustomerDiscountProgress = ({
	accumulatedCount = 0,
	currentTicketCount = 0,
	requiredCount = 0,
	freeCount = 0
}) => {
	const threshold = getNumberValue(requiredCount) + getNumberValue(freeCount);
	const totalForCurrentPurchase = getNumberValue(accumulatedCount) + getNumberValue(currentTicketCount);
	const totalSets = threshold > 0 ? Math.floor(totalForCurrentPurchase / threshold) : 0;

	return {
		threshold,
		totalForCurrentPurchase,
		totalSets
	};
};

export const shouldRefreshSaleConfigBeforeCreateOrder = (createdOrderId) => !createdOrderId;

const BuyPowerball = ({ onLogout, userRole }) => {
	const [activeTab, setActiveTab] = useState(null);
	// แยก tickets สำหรับแต่ละประเภท
	const [powerballTickets, setPowerballTickets] = useState([]);
	const [megaMillionsTickets, setMegaMillionsTickets] = useState([]);
	const [showNumberModal, setShowNumberModal] = useState(false);
	const [showSummaryModal, setShowSummaryModal] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [showCloseBuyAlert, setShowCloseBuyAlert] = useState(false);
	const [currentTicketIndex, setCurrentTicketIndex] = useState(null);
	const [paymentMethod, setPaymentMethod] = useState('scan');
	const [useDiscountPB, setUseDiscountPB] = useState(false);
	const [useDiscountMG, setUseDiscountMG] = useState(false);
	const [useDiscountPBOld, setUseDiscountPBOld] = useState(false);
	const [useDiscountMGOld, setUseDiscountMGOld] = useState(false);
	const [slipFile, setSlipFile] = useState(null);
	const [slipPreview, setSlipPreview] = useState(null);
	const [createdOrderId, setCreatedOrderId] = useState(null);
	// config จาก api
	const [PRICE_PER_TICKET_PB, setPricePerTicketPB] = useState(0);
	const [PRICE_PER_TICKET_MG, setPricePerTicketMG] = useState(0);
	const [lot_pb_date, setLotPbDate] = useState(null);
	const [lot_mg_date, setLotMgDate] = useState(null);
	const [disabledTabPB, setDisabledTabPB] = useState(true);
	const [disabledTabMG, setDisabledTabMG] = useState(true);
	const [isPowerballActive, setIsPowerballActive] = useState(false);
	const [isMegaMillionsActive, setIsMegaMillionsActive] = useState(false);
	// รายละเอียดสมาชิก
	const [customer_mg_new_count, setCustomerMgNewCount] = useState(0);
	const [customer_mg_new_free, setCustomerMgNewFree] = useState(0);
	const [customer_mg_old_count, setCustomerMgOldCount] = useState(0);
	const [customer_mg_old_free, setCustomerMgOldFree] = useState(0);
	const [customer_pb_new_count, setCustomerPbNewCount] = useState(0);
	const [customer_pb_new_free, setCustomerPbNewFree] = useState(0);
	const [customer_pb_old_count, setCustomerPbOldCount] = useState(0);
	const [customer_pb_old_free, setCustomerPbOldFree] = useState(0);
	const [discount_pb_new_customer_status, setDiscountPbNewCustomerStatus] = useState(0);
	const [discount_pb_new_customer_order_number, setDiscountPbNewCustomerOrderNumber] = useState(0);
	const [discount_pb_order_number, setDiscountPbOrderNumber] = useState(0);
	const [discount_mg_new_customer_status, setDiscountMgNewCustomerStatus] = useState(0);
	const [discount_mg_new_customer_order_number, setDiscountMgNewCustomerOrderNumber] = useState(0);
	const [discount_mg_order_number, setDiscountMgOrderNumber] = useState(0);
	const [pay_bank_name, setPayBankName] = useState('');
	const [pay_bank_account, setPayBankAccount] = useState('');
	const [pay_bank_account_name, setPayBankAccountName] = useState('');
	const [pay_prompt_pay, setPayPromptPay] = useState('');
	const [pay_prompt_pay_name, setPayPromptPayName] = useState('');

	// Lock body scroll when any modal is open
	useBodyScrollLock(showNumberModal || showSummaryModal || showPaymentModal);

	// ใช้ tickets ตามประเภทที่เลือก
	const tickets = activeTab === 'pb' ? powerballTickets : megaMillionsTickets;
	const setTickets = activeTab === 'pb' ? setPowerballTickets : setMegaMillionsTickets;

  	const fetchAwardActive = async () => {
		try {
			const token = localStorage.getItem('token');
			if (!token) return;

			const response = await fetch(`${API_CONFIG.BASE_URL}/awards/award-active`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});
			if (response.ok) {
				const result = await response.json();
				const pbActive = normalizeActiveFlag(result.pb.active);
				const mgActive = normalizeActiveFlag(result.mg.active);
				setPricePerTicketPB(result.pb.price || 0);
				setPricePerTicketMG(result.mg.price || 0);
				setLotPbDate(result.pb.prize_announcement_date || null);
				setLotMgDate(result.mg.prize_announcement_date || null);
				setDisabledTabPB(!pbActive);
				setDisabledTabMG(!mgActive);
				setIsPowerballActive(pbActive);
				setIsMegaMillionsActive(mgActive);
				setActiveTab(currentTab => resolveActiveTab(currentTab, pbActive, mgActive));
				if(!pbActive && !mgActive){
					setShowCloseBuyAlert(true);
				}
			}else{
				setShowCloseBuyAlert(true);
			}
		} catch (error) {
			console.error('Error fetching LINE ID:', error);
		}
	};

	const fetchConfig = async () => {
		try {
			const token = localStorage.getItem('token');
			if (!token) return null;

			const response = await fetch(`${API_CONFIG.BASE_URL}/config/sale`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});
			if (response.ok) {
				const result = await response.json();
				const data = result.data || {};
				setCustomerMgNewCount(data.customer_mg_new_count || 0);
				setCustomerMgNewFree(data.customer_mg_new_free || 0);
				setCustomerMgOldCount(data.customer_mg_old_count || 0);
				setCustomerMgOldFree(data.customer_mg_old_free || 0);
				setCustomerPbNewCount(data.customer_pb_new_count || 0);
				setCustomerPbNewFree(data.customer_pb_new_free || 0);
				setCustomerPbOldCount(data.customer_pb_old_count || 0);
				setCustomerPbOldFree(data.customer_pb_old_free || 0);
				setDiscountPbNewCustomerOrderNumber(data.discount_pb_new_customer_order_number || 0);
				setDiscountPbNewCustomerStatus(data.discount_pb_new_customer_status || 0);
				setDiscountPbOrderNumber(data.discount_pb_order_number || 0);
				setDiscountMgNewCustomerOrderNumber(data.discount_mg_new_customer_order_number || 0);
				setDiscountMgNewCustomerStatus(data.discount_mg_new_customer_status || 0);
				setDiscountMgOrderNumber(data.discount_mg_order_number || 0);
				setPayBankName(data.bank_name || '');
				setPayBankAccount(data.bank_account || '');
				setPayBankAccountName(data.bank_account_name || '');
				setPayPromptPay(data.prompt_pay || '');
				setPayPromptPayName(data.prompt_pay_name || '');
				return data;
			}
			return null;
		} catch (error) {
			console.error('Error fetching LINE ID:', error);
			return null;
		}
	};

	// Add initial ticket on mount for both types
	useEffect(() => {
		fetchAwardActive();
		fetchConfig();
		if (powerballTickets.length === 0) {
			setPowerballTickets([{
				id: Date.now(),
				type: 'pb',
				whiteBalls: ['', '', '', '', ''],
				powerBall: ''
			}]);
		}
		if (megaMillionsTickets.length === 0) {
			setMegaMillionsTickets([{
				id: Date.now() + 1,
				type: 'mg',
				whiteBalls: ['', '', '', '', ''],
				powerBall: ''
			}]);
		}
		if(showCloseBuyAlert){
			showWarning(null, 'ขณะนี้ยังไม่ได้เปิดให้ทำการซื้อเลข กรุณาลองใหม่ในภายหลัง', {
				showCloseButton: false,
				showCancelButton: false,
				allowOutsideClick: false,
				timer: 5000,
				timerProgressBar: true,
				didClose: () => {
					window.history.back();
				}
			});
		}
	}, [
		powerballTickets.length,
		megaMillionsTickets.length,
		showCloseBuyAlert
	]);

  const addNewTicket = () => {
    setTickets([...tickets, {
      id: Date.now(),
      type: activeTab,
      whiteBalls: ['', '', '', '', ''],
      powerBall: ''
    }]);
  };

  const copyTicket = (sourceIndex, targetIndex) => {
    const newTickets = [...tickets];
    newTickets[targetIndex] = {
      ...newTickets[targetIndex],
      whiteBalls: [...tickets[sourceIndex].whiteBalls],
      powerBall: tickets[sourceIndex].powerBall
    };
    setTickets(newTickets);
  };

  const removeTicket = (index) => {
    const newTickets = tickets.filter((_, i) => i !== index);
    setTickets(newTickets);
  };

  const randomizeTicket = (index) => {
    const newTickets = [...tickets];
    const ticketType = tickets[index].type || activeTab;

    // Generate 5 unique random white balls
    // Powerball: 1-69, Mega Millions: 1-75
    const maxWhite = ticketType === 'pb' ? 69 : 70;
    const whiteBalls = [];
    while (whiteBalls.length < 5) {
      const num = Math.floor(Math.random() * maxWhite) + 1;
      if (!whiteBalls.includes(num)) {
        whiteBalls.push(num);
      }
    }

    // Generate 1 random powerball
    // Powerball: 1-26, Mega Millions: 1-46
    const maxPower = ticketType === 'pb' ? 26 : 24;
    const powerBall = Math.floor(Math.random() * maxPower) + 1;

    newTickets[index] = {
      ...newTickets[index],
      whiteBalls: whiteBalls.map(n => String(n)),
      powerBall: String(powerBall)
    };

    setTickets(newTickets);
  };

  const clearTicketNumbers = (index) => {
    const newTickets = [...tickets];
    newTickets[index] = {
      ...newTickets[index],
      whiteBalls: ['', '', '', '', ''],
      powerBall: ''
    };
    setTickets(newTickets);
  };

  const openNumberModal = (index) => {
    setCurrentTicketIndex(index);
    setShowNumberModal(true);
  };

  const updateTicketNumber = (index, ballIndex, value) => {
    const newTickets = [...tickets];
    newTickets[index].whiteBalls[ballIndex] = value;
    setTickets(newTickets);
  };

  const updateTicketPowerBall = (index, value) => {
    const newTickets = [...tickets];
    newTickets[index].powerBall = value;
    setTickets(newTickets);
  };

  // ฟังก์ชันตรวจสอบว่าตั๋วกรอกครบหรือไม่
  const isTicketComplete = (ticket) => {
    return ticket.whiteBalls.every(ball => ball !== '') && ticket.powerBall !== '';
  };

  // นับเฉพาะตั๋วที่กรอกครบ
  const validPowerballTickets = powerballTickets.filter(isTicketComplete);
  const validMegaMillionsTickets = megaMillionsTickets.filter(isTicketComplete);
  const validTicketsCount = validPowerballTickets.length + validMegaMillionsTickets.length;
  const currentValidTicketsCount = activeTab === 'pb' ? validPowerballTickets.length : validMegaMillionsTickets.length;
  const currentSaleConfig = {
    customer_mg_new_count,
    customer_mg_new_free,
    customer_mg_old_count,
    customer_mg_old_free,
    discount_mg_new_customer_status,
    discount_mg_new_customer_order_number,
    discount_mg_order_number,
    customer_pb_new_count,
    customer_pb_new_free,
    customer_pb_old_count,
    customer_pb_old_free,
    discount_pb_new_customer_status,
    discount_pb_new_customer_order_number,
    discount_pb_order_number
  };

  // คำนวณส่วนลด
  const getDiscountInfo = (saleConfig = currentSaleConfig) => calculateDiscountInfo({
    validPowerballTickets,
    validMegaMillionsTickets,
    useDiscountPB,
    useDiscountMG,
    useDiscountPBOld,
    useDiscountMGOld,
    pricePerTicketPB: PRICE_PER_TICKET_PB,
    pricePerTicketMG: PRICE_PER_TICKET_MG,
    saleConfig
  });
  const getTotalPriceAllTickets = (discountInfoForTotal = discountInfo) => calculateTotalPriceAllTickets({
    validPowerballTickets,
    validMegaMillionsTickets,
    pricePerTicketPB: PRICE_PER_TICKET_PB,
    pricePerTicketMG: PRICE_PER_TICKET_MG,
    discountInfo: discountInfoForTotal
  });

  const discountInfo = getDiscountInfo();
  const totalPriceAllTickets = getTotalPriceAllTickets(discountInfo);

  const allTicketsCount = powerballTickets.length + megaMillionsTickets.length;

  useEffect(() => {
    const pbNewThreshold = getNumberValue(customer_pb_new_count) + getNumberValue(customer_pb_new_free);
    const mgNewThreshold = getNumberValue(customer_mg_new_count) + getNumberValue(customer_mg_new_free);
    const pbOldThreshold = getNumberValue(customer_pb_old_count) + getNumberValue(customer_pb_old_free);
    const mgOldThreshold = getNumberValue(customer_mg_old_count) + getNumberValue(customer_mg_old_free);

    if (useDiscountPB && (getNumberValue(discount_pb_new_customer_status) !== 0 || validPowerballTickets.length < pbNewThreshold)) {
      setUseDiscountPB(false);
    }
    if (useDiscountMG && (getNumberValue(discount_mg_new_customer_status) !== 0 || validMegaMillionsTickets.length < mgNewThreshold)) {
      setUseDiscountMG(false);
    }
    if (useDiscountPBOld && (
      getNumberValue(discount_pb_new_customer_status) !== 1 ||
      pbOldThreshold <= 0 ||
      getNumberValue(discount_pb_order_number) + validPowerballTickets.length < pbOldThreshold
    )) {
      setUseDiscountPBOld(false);
    }
    if (useDiscountMGOld && (
      getNumberValue(discount_mg_new_customer_status) !== 1 ||
      mgOldThreshold <= 0 ||
      getNumberValue(discount_mg_order_number) + validMegaMillionsTickets.length < mgOldThreshold
    )) {
      setUseDiscountMGOld(false);
    }
  }, [
    useDiscountPB,
    useDiscountMG,
    useDiscountPBOld,
    useDiscountMGOld,
    customer_pb_new_count,
    customer_pb_new_free,
    customer_mg_new_count,
    customer_mg_new_free,
    customer_pb_old_count,
    customer_pb_old_free,
    customer_mg_old_count,
    customer_mg_old_free,
    discount_pb_new_customer_status,
    discount_mg_new_customer_status,
    discount_pb_order_number,
    discount_mg_order_number,
    validPowerballTickets.length,
    validMegaMillionsTickets.length
  ]);

  // ฟังก์ชันสำหรับนับตั๋วที่ถูกต้อง
  const getValidTicketsCount = () => {
    const validPowerball = powerballTickets.filter(isTicketComplete);
    const validMegaMillions = megaMillionsTickets.filter(isTicketComplete);
    return validPowerball.length + validMegaMillions.length;
  };

  const handleSubmit = async () => {
    const validCount = getValidTicketsCount();
    if (validCount === 0) {
		showWarning('แจ้งเตือน', 'กรุณาเลือกตัวเลขให้ครบก่อนยืนยันคำสั่งซื้อ');
      	return;
    }
    const refreshedSaleConfig = await fetchConfig();
    if (!refreshedSaleConfig) {
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดยอดส่วนลดล่าสุดได้ กรุณาลองใหม่อีกครั้ง');
      return;
    }
    setShowSummaryModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
		showError('ไฟล์ไม่ถูกต้อง', 'กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
		showError('ไฟล์ใหญ่เกินไป', 'กรุณาอัพโหลดไฟล์ไม่เกิน 5MB');
        return;
      }
      setSlipFile(file);
      // สร้าง preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setSlipPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveSlip = () => {
    setSlipFile(null);
    setSlipPreview(null);
  };

  const buildOrderDiscount = (saleConfigForOrder, discountInfoForOrder) => ({
    pb: {
      useDiscount: useDiscountPB || useDiscountPBOld,
      isNewCustomer: getNumberValue(saleConfigForOrder.discount_pb_new_customer_status) === 0,
      isOldCustomer: getNumberValue(saleConfigForOrder.discount_pb_new_customer_status) === 1 && useDiscountPBOld,
      freeTickets: discountInfoForOrder.pbFreeTickets,
      discountAmount: discountInfoForOrder.pbDiscountAmount,
      requiredCount: useDiscountPB ? getNumberValue(saleConfigForOrder.customer_pb_new_count) : getNumberValue(saleConfigForOrder.customer_pb_old_count),
      freeCount: useDiscountPB ? getNumberValue(saleConfigForOrder.customer_pb_new_free) : getNumberValue(saleConfigForOrder.customer_pb_old_free)
    },
    mg: {
      useDiscount: useDiscountMG || useDiscountMGOld,
      isNewCustomer: getNumberValue(saleConfigForOrder.discount_mg_new_customer_status) === 0,
      isOldCustomer: getNumberValue(saleConfigForOrder.discount_mg_new_customer_status) === 1 && useDiscountMGOld,
      freeTickets: discountInfoForOrder.mgFreeTickets,
      discountAmount: discountInfoForOrder.mgDiscountAmount,
      requiredCount: useDiscountMG ? getNumberValue(saleConfigForOrder.customer_mg_new_count) : getNumberValue(saleConfigForOrder.customer_mg_old_count),
      freeCount: useDiscountMG ? getNumberValue(saleConfigForOrder.customer_mg_new_free) : getNumberValue(saleConfigForOrder.customer_mg_old_free)
    }
  });

  const handleConfirmOrder = async () => {
    const shouldRefreshSaleConfig = shouldRefreshSaleConfigBeforeCreateOrder(createdOrderId);
    const saleConfigForOrder = shouldRefreshSaleConfig ? await fetchConfig() : currentSaleConfig;
    if (!saleConfigForOrder) {
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดยอดส่วนลดล่าสุดได้ กรุณาลองใหม่อีกครั้ง');
      return;
    }

    const latestDiscountInfo = getDiscountInfo(saleConfigForOrder);
    const latestTotalPriceAllTickets = getTotalPriceAllTickets(latestDiscountInfo);

	if(useDiscountPB && powerballTickets.length < (getNumberValue(saleConfigForOrder.customer_pb_new_count) + getNumberValue(saleConfigForOrder.customer_pb_new_free))){
		showWarning('แจ้งเตือน', 'กรุณาเลขให้ครบถ้วน');
      	return;
	}
	if(useDiscountMG && megaMillionsTickets.length < (getNumberValue(saleConfigForOrder.customer_mg_new_count) + getNumberValue(saleConfigForOrder.customer_mg_new_free))){
		showWarning('แจ้งเตือน', 'กรุณาเลขให้ครบถ้วน');
		return;
	}

    // ตรวจสอบว่าราคาหลังหักส่วนลดเป็น 0 หรือไม่
    if (latestTotalPriceAllTickets === 0) {
      // ถ้าราคาเป็น 0 ให้บันทึก order แบบส่วนลดทันที
      handleConfirmPurchase(saleConfigForOrder, latestDiscountInfo, latestTotalPriceAllTickets);
      return;
    }

    // ตรวจสอบว่าเลือกวิธีจ่ายเงินแล้วหรือยัง
    if (!paymentMethod) {
      showWarning('กรุณาเลือกวิธีชำระเงิน', 'กรุณาเลือกวิธีชำระเงินก่อนดำเนินการต่อ');
      return;
    }

    // ถ้าเลือกสแกนจ่าย ให้สร้าง/อัพเดต order ก่อน (approve_status=0)
    if (paymentMethod === 'scan') {
      try {
        const totalPrice = (validPowerballTickets.length * PRICE_PER_TICKET_PB) + (validMegaMillionsTickets.length * PRICE_PER_TICKET_MG);

        // เตรียมข้อมูลการซื้อ
        const allValidTickets = [
          ...validPowerballTickets.map(t => ({ ...t, type: 'pb' })),
          ...validMegaMillionsTickets.map(t => ({ ...t, type: 'mg' }))
        ];

        const orderData = {
          tickets: allValidTickets,
          paymentMethod: paymentMethod,
          totalPrice: totalPrice,
          createOnly: true, // บอก API ว่าให้สร้าง order อย่างเดียว ยังไม่มีสลิป
          orderId: createdOrderId, // ส่ง orderId ไปด้วย (ถ้ามี) เพื่ออัพเดต
          discount: buildOrderDiscount(saleConfigForOrder, latestDiscountInfo)
        };

        // เรียก API สร้าง/อัพเดต order
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.BASE_URL}/orders/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(orderData)
        });

        const resultData = await response.json();

        if (response.ok && resultData.success) {
          // เก็บ orderId ที่สร้าง/อัพเดตไว้
          setCreatedOrderId(resultData.orderId);
          // เปิด modal payment
          setShowSummaryModal(false);
          setShowPaymentModal(true);
        } else {
          showError('เกิดข้อผิดพลาด', resultData.message || 'ไม่สามารถสร้างคำสั่งซื้อได้');
        }
      } catch (error) {
        console.error('Error creating order:', error);
        showError('เกิดข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้');
      }
    } else {
      // ถ้าเลือกส่วนลด ส่งข้อมูลเลย
      handleConfirmPurchase(saleConfigForOrder, latestDiscountInfo, latestTotalPriceAllTickets);
    }
  };

  const handleConfirmPurchase = async (saleConfigForOrder = currentSaleConfig, discountInfoForOrder = discountInfo, totalPriceForOrder = totalPriceAllTickets) => {
    // กำหนด paymentMethod สำหรับกรณีที่ราคาเป็น 0 (ใช้ส่วนลดทั้งหมด)
    const actualPaymentMethod = totalPriceForOrder === 0 ? 'discount' : paymentMethod;

    // กรณีใช้ส่วนลดจนราคาเป็น 0 - สร้าง order ทันที
    if (actualPaymentMethod === 'discount') {
      const result = await showConfirm({
        title: 'ยืนยันการสั่งซื้อ',
        html: `คุณต้องการยืนยันการสั่งซื้อหรือไม่?<br><span class="text-green-600">ใช้ส่วนลดทั้งหมด ไม่มีค่าใช้จ่าย</span>`
      });

      if (result.isConfirmed) {
        try {
          const totalPrice = (validPowerballTickets.length * PRICE_PER_TICKET_PB) + (validMegaMillionsTickets.length * PRICE_PER_TICKET_MG);

          const allValidTickets = [
            ...validPowerballTickets.map(t => ({ ...t, type: 'pb' })),
            ...validMegaMillionsTickets.map(t => ({ ...t, type: 'mg' }))
          ];

          const orderData = {
            tickets: allValidTickets,
            paymentMethod: 'discount',
            totalPrice: 0,
            totalPriceBeforeDiscount: totalPrice,
            discount: buildOrderDiscount(saleConfigForOrder, discountInfoForOrder)
          };

          const token = localStorage.getItem('token');
          const response = await fetch(`${API_CONFIG.BASE_URL}/orders/create`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
          });

          const resultData = await response.json();

          if (response.ok && resultData.success) {
            setShowSummaryModal(false);
            setShowPaymentModal(false);
            resetAll();
            showSuccess('สั่งซื้อสำเร็จ', 'คำสั่งซื้อของคุณรอการตรวจสอบจากผู้ดูแล');
          } else {
            showError('เกิดข้อผิดพลาด', resultData.message || 'ไม่สามารถสร้างคำสั่งซื้อได้');
          }
        } catch (error) {
          console.error('Error creating discount order:', error);
          showError('เกิดข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้');
        }
      }
      return;
    }

    // ตรวจสอบวิธีชำระเงิน
    if (paymentMethod === 'scan') {
      // สแกนจ่าย - อัพเดต order ที่สร้างไว้แล้ว
      if (!slipFile) {
        showError('กรุณาอัพโหลดสลิป', 'กรุณาอัพโหลดหลักฐานการชำระเงินก่อนยืนยัน');
        return;
      }

      if (!createdOrderId) {
        showError('เกิดข้อผิดพลาด', 'ไม่พบข้อมูลคำสั่งซื้อ กรุณาลองใหม่อีกครั้ง');
        return;
      }

      const result = await showConfirm({
        title: 'ยืนยันการส่งสลิป',
        html: `คุณต้องการยืนยันการชำระเงินหรือไม่?<br><span class="text-gray-500">หลังจากส่งสลิปแล้ว คำสั่งซื้อจะอยู่ระหว่างรอการตรวจสอบจากผู้ดูแล</span>`
      });

      if (result.isConfirmed) {
        try {
          // สร้าง FormData สำหรับส่งสลิป
          const formData = new FormData();
          formData.append('orderId', createdOrderId);
          formData.append('slip', slipFile);

          // เรียก API อัพเดต order
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_CONFIG.BASE_URL}/orders/upload-slip`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          const resultData = await response.json();

          if (response.ok && resultData.success) {
            setShowSummaryModal(false);
            setShowPaymentModal(false);
            resetAll();
            showSuccess('ส่งสลิปเรียบร้อย', 'คำสั่งซื้อของคุณอยู่ระหว่างรอการตรวจสอบ');
          } else {
            showError('เกิดข้อผิดพลาด', resultData.message || 'ไม่สามารถอัพโหลดสลิปได้');
          }
        } catch (error) {
          console.error('Error uploading slip:', error);
          showError('เกิดข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้');
        }
      }
    }
  };

	const resetAll = useCallback(() => {
    resetPurchaseState({
      setPowerballTickets,
      setMegaMillionsTickets,
      setPaymentMethod,
      setSlipFile,
      setSlipPreview,
      setCreatedOrderId,
      setActiveTab,
      setUseDiscountPB,
      setUseDiscountMG,
      setUseDiscountPBOld,
      setUseDiscountMGOld,
      isPowerballActive,
      isMegaMillionsActive
    });
  }, [isPowerballActive, isMegaMillionsActive]);

  if (!['sysadmin', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  return (
    <Layout title="สั่งซื้อ" onLogout={onLogout} userRole={userRole}>
      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('pb')}
              disabled={disabledTabPB}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'pb'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              } ${disabledTabPB ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {LOTTO_TYPE.powerball}
            </button>
            <button
              onClick={() => setActiveTab('mg')}
              disabled={disabledTabMG}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'mg'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              } ${disabledTabMG ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {LOTTO_TYPE.megamillions}
            </button>
          </div>
        </div>

        {/* Tickets List */}
        <div className="space-y-4 mb-6">

			<h3 className="text-md font-semibold text-gray-900 dark:text-white">
                {activeTab === 'pb' ? (`งวด${lot_pb_date ? formatDateFLot(parseDatetimeLocal(lot_pb_date)) : '..'}`) : (`งวด${lot_mg_date ? formatDateFLot(parseDatetimeLocal(lot_mg_date)) : '..'}`)}
            </h3>

          {tickets.map((ticket, index) => (

            <div key={ticket.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    ใบที่ {index + 1}
                  </h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({ticket.type === 'pb' ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions})
                  </span>
                </div>
                {tickets.length > 1 && (
                  <button
                    onClick={() => removeTicket(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    aria-label="ลบ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Display Selected Numbers with Input */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex gap-2">
                    {ticket.whiteBalls.map((num, ballIndex) => (
                      <div key={ballIndex} className="flex flex-col items-center">
                        <input
                          type="number"
                          min="1"
                          max={ticket.type === 'pb' ? 69 : 75}
                          value={num}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              updateTicketNumber(index, ballIndex, '');
                              return;
                            }
                            const numValue = parseInt(value);
                            const maxWhite = ticket.type === 'pb' ? 69 : 75;
                            if (!isNaN(numValue) && numValue >= 1 && numValue <= maxWhite) {
                              const isDuplicate = ticket.whiteBalls.some((ball, idx) => idx !== ballIndex && ball === String(numValue));
                              if (!isDuplicate) {
                                updateTicketNumber(index, ballIndex, String(numValue));
                              }
                            }
                          }}
                          placeholder="-"
                          className={`w-12 h-12 text-center rounded-full font-bold text-lg border-2 ${
                            num
                              ? (
								ticket.type === 'pb' ?
								'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-300 dark:to-gray-400 border-gray-400 text-gray-900'
								:
								'bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-300 dark:to-blue-400 border-blue-400 text-blue-900'
							  )
                              : (
								ticket.type === 'pb' ?
								'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600'
								:
								'bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600 text-blue-400 dark:text-blue-600'
							  )
                          } focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                      </div>
                    ))}
                  </div>
                  <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">+</span>
                  <div className="flex flex-col items-center">
                    <input
                      type="number"
                      min="1"
                      max={ticket.type === 'pb' ? 26 : 46}
                      value={ticket.powerBall}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          updateTicketPowerBall(index, '');
                          return;
                        }
                        const numValue = parseInt(value);
                        const maxPower = ticket.type === 'pb' ? 26 : 46;
                        if (!isNaN(numValue) && numValue >= 1 && numValue <= maxPower) {
                          updateTicketPowerBall(index, String(numValue));
                        }
                      }}
                      placeholder="-"
                      className={`w-12 h-12 text-center rounded-full font-bold text-lg border-2 ${
                        ticket.powerBall
                          ? (
								ticket.type === 'pb' ?
								'bg-gradient-to-br from-red-500 to-red-700 border-red-600 text-white shadow-lg shadow-red-500/30'
								:
								'bg-gradient-to-br from-yellow-500 to-yellow-700 border-yellow-600 text-white shadow-lg shadow-yellow-500/30'
							)
                          : (
								ticket.type === 'pb' ?
								'bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-400 dark:text-red-600'
								:
								'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 text-yellow-400 dark:text-yellow-600'
							)
                      } ${ticket.type === 'pb' ?
						'focus:ring-2 focus:ring-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' :
						'focus:ring-2 focus:ring-yellow-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'}`}
                    />
                  </div>
                </div>
				<div className="text-right">
					<p className="text-sm text-gray-600 dark:text-gray-400">ราคาต่อใบ</p>
					<p className={`text-3xl font-bold ${
						isTicketComplete(ticket)
							? 'text-blue-600 dark:text-blue-400'
							: 'text-gray-400 dark:text-gray-600'
					}`}>
						{activeTab === 'pb' ? PRICE_PER_TICKET_PB : PRICE_PER_TICKET_MG} บาท
					</p>
				</div>
              </div>

              {/* Action Buttons */}
			  {!disabledTabPB || !disabledTabMG ? (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => openNumberModal(index)}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                >
                  เลือกเลข
                </button>
                <button
                  onClick={() => randomizeTicket(index)}
                  className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors"
                >
                  สุ่มเลข
                </button>
                <button
                  type="button"
                  onClick={() => clearTicketNumbers(index)}
                  className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                >
                  ล้าง
                </button>
                {tickets.length > 1 && (
                  <div className="relative group">
                    <button
                      className="px-3 py-1.5 text-sm bg-purple-500 hover:bg-purple-700 text-white font-medium rounded-md transition-colors"
                    >
                      คัดลอกจาก
                    </button>
                    <div className="absolute left-0 mt-0 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 hidden group-hover:block z-10">
                      {tickets.map((t, idx) => {
                        if (idx === index) return null;
                        const hasNumbers = t.whiteBalls.some(n => n) || t.powerBall;
                        return (
                          <button
                            key={t.id}
                            onClick={() => copyTicket(idx, index)}
                            disabled={!hasNumbers}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed first:rounded-t-lg last:rounded-b-lg transition-colors text-sm"
                          >
                            ใบที่ {idx + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>):<></>}
            </div>
          ))}
        </div>

        {/* Add Ticket Button */}
		{!disabledTabPB || !disabledTabMG ? (
			<div className="mb-6">
			<button
				onClick={addNewTicket}
				className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors border-2 border-dashed border-gray-300 dark:border-gray-600"
			>
				+ เพิ่มใบซื้อ
			</button>
			</div>
		) : <></>}

        {/* Total Price Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">จำนวนใบ {activeTab === 'pb' ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentValidTicketsCount} ใบ</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden">
                รวมทั้งหมด: {allTicketsCount} ใบ (PB: {powerballTickets.length}, MG: {megaMillionsTickets.length})
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 font-semibold mt-1">
                รวมใบที่กรอกครบแล้ว: {validTicketsCount} ใบ (PB: {validPowerballTickets.length}, MG: {validMegaMillionsTickets.length})
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-gray-400">ราคารวมทั้งหมด</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalPriceAllTickets.toLocaleString()}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">บาท</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
		{!disabledTabPB || !disabledTabMG ? (
			<div className="flex justify-end gap-4">
				<button
					onClick={handleSubmit}
					className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-lg"
				>
					ยืนยันการซื้อ ({totalPriceAllTickets.toLocaleString()} บาท)
				</button>
			</div>
		) : <></>}
      </div>

      {/* Number Selection Modal */}
      {showNumberModal && currentTicketIndex !== null && (
        <NumberSelectionModal
          ticket={tickets[currentTicketIndex]}
          ticketType={tickets[currentTicketIndex].type || activeTab}
          onUpdate={(whiteBalls, powerBall) => {
            const newTickets = [...tickets];
            newTickets[currentTicketIndex] = {
              ...newTickets[currentTicketIndex],
              whiteBalls,
              powerBall
            };
            setTickets(newTickets);
          }}
          onClose={() => setShowNumberModal(false)}
        />
      )}

      {/* Summary Modal */}
      {showSummaryModal && (
        <SummaryModal
          powerballTickets={validPowerballTickets}
          megaMillionsTickets={validMegaMillionsTickets}
          totalPrice={totalPriceAllTickets}
          pricePerTicketPB={PRICE_PER_TICKET_PB}
          pricePerTicketMG={PRICE_PER_TICKET_MG}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          slipFile={slipFile}
          slipPreview={slipPreview}
		  lot_pb_date={lot_pb_date}
		  lot_mg_date={lot_mg_date}
		  pay_bank_name={pay_bank_name}
		  pay_bank_account={pay_bank_account}
		  pay_bank_account_name={pay_bank_account_name}
		  pay_prompt_pay={pay_prompt_pay}
		  pay_prompt_pay_name={pay_prompt_pay_name}
		  customer_mg_new_count={customer_mg_new_count}
		  customer_mg_new_free={customer_mg_new_free}
		  customer_mg_old_count={customer_mg_old_count}
		  customer_mg_old_free={customer_mg_old_free}
		  discount_mg_new_customer_status={discount_mg_new_customer_status}
		  discount_mg_new_customer_order_number={discount_mg_new_customer_order_number}
		  discount_mg_order_number={discount_mg_order_number}
		  customer_pb_new_count={customer_pb_new_count}
		  customer_pb_new_free={customer_pb_new_free}
		  customer_pb_old_count={customer_pb_old_count}
		  customer_pb_old_free={customer_pb_old_free}
		  discount_pb_new_customer_status={discount_pb_new_customer_status}
		  discount_pb_new_customer_order_number={discount_pb_new_customer_order_number}
		  discount_pb_order_number={discount_pb_order_number}
		  useDiscountPB={useDiscountPB}
		  setUseDiscountPB={setUseDiscountPB}
		  useDiscountMG={useDiscountMG}
		  setUseDiscountMG={setUseDiscountMG}
		  useDiscountPBOld={useDiscountPBOld}
		  setUseDiscountPBOld={setUseDiscountPBOld}
		  useDiscountMGOld={useDiscountMGOld}
		  setUseDiscountMGOld={setUseDiscountMGOld}
		  discountInfo={discountInfo}
          handleFileChange={handleFileChange}
          handleRemoveSlip={handleRemoveSlip}
          onConfirm={() => handleConfirmOrder()}
          onClose={() => setShowSummaryModal(false)}
        />
      )}

      {/* Payment Modal (QR Code & Slip Upload) */}
      {showPaymentModal && (
        <PaymentModal
          totalPrice={totalPriceAllTickets}
          pay_bank_name={pay_bank_name}
          pay_bank_account={pay_bank_account}
          pay_bank_account_name={pay_bank_account_name}
          pay_prompt_pay={pay_prompt_pay}
          pay_prompt_pay_name={pay_prompt_pay_name}
          slipFile={slipFile}
          slipPreview={slipPreview}
          handleFileChange={handleFileChange}
          handleRemoveSlip={handleRemoveSlip}
          onConfirm={() => handleConfirmPurchase()}
          onClose={() => {
            setShowPaymentModal(false);
            setShowSummaryModal(true);
          }}
          onCloseAll={() => {
            setShowPaymentModal(false);
            setShowSummaryModal(false);
          }}
		  resetAll={resetAll}
        />
      )}
    </Layout>
  );
};

// Summary Modal Component
const SummaryModal = ({
  powerballTickets,
  megaMillionsTickets,
  totalPrice,
  pricePerTicketPB,
  pricePerTicketMG,
  paymentMethod,
  setPaymentMethod,
  slipFile,
  slipPreview,
  lot_pb_date,
  lot_mg_date,
  pay_bank_name,
  pay_bank_account,
  pay_bank_account_name,
  pay_prompt_pay,
  pay_prompt_pay_name,
  customer_mg_new_count,
  customer_mg_new_free,
  customer_mg_old_count,
  customer_mg_old_free,
  discount_mg_new_customer_status,
  discount_mg_new_customer_order_number,
  discount_mg_order_number,
  customer_pb_new_count,
  customer_pb_new_free,
  customer_pb_old_count,
  customer_pb_old_free,
  discount_pb_new_customer_status,
  discount_pb_new_customer_order_number,
  discount_pb_order_number,
  useDiscountPB,
  setUseDiscountPB,
  useDiscountMG,
  setUseDiscountMG,
  useDiscountPBOld,
  setUseDiscountPBOld,
  useDiscountMGOld,
  setUseDiscountMGOld,
  discountInfo,
  handleFileChange,
  handleRemoveSlip,
  onConfirm,
  onClose
}) => {
  const allTickets = [
    ...powerballTickets.map(t => ({ ...t, type: 'pb' })),
    ...megaMillionsTickets.map(t => ({ ...t, type: 'mg' }))
  ];
  const pbOldDiscountProgress = calculateOldCustomerDiscountProgress({
	accumulatedCount: discount_pb_order_number,
	currentTicketCount: powerballTickets.length,
	requiredCount: customer_pb_old_count,
	freeCount: customer_pb_old_free
  });
  const mgOldDiscountProgress = calculateOldCustomerDiscountProgress({
	accumulatedCount: discount_mg_order_number,
	currentTicketCount: megaMillionsTickets.length,
	requiredCount: customer_mg_old_count,
	freeCount: customer_mg_old_free
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg my-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">สรุปรายการซื้อเลข</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="ปิด"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        	<div className="p-6 space-y-4">
				{/* Powerball Tickets */}
				{powerballTickets.length > 0 && (
					<div>
						<h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
							<span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm">{LOTTO_TYPE.powerball}</span>
							<span className="text-sm text-gray-600 dark:text-gray-400">({powerballTickets.length} ใบ)</span>
						</h4>
						<h3 className="my-3 text-gray-900 dark:text-white">
							งวด{lot_pb_date ? formatDateFLot(parseDatetimeLocal(lot_pb_date)) : '..'}
						</h3>
						<div className="space-y-3">
							{powerballTickets.map((ticket, index) => (
							<div key={ticket.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">ใบที่ {index + 1}</span>
								<span className="text-sm text-red-600 dark:text-red-400 font-semibold">{pricePerTicketPB} บาท</span>
								</div>
								<div className="flex flex-wrap items-center gap-2">
								{ticket.whiteBalls.map((num, ballIndex) => (
									<div
									key={ballIndex}
									className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
										num
										? 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-300 dark:to-gray-400 text-gray-900'
										: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
									}`}
									>
									{num || '-'}
									</div>
								))}
								<span className="text-xl font-bold text-gray-600 dark:text-gray-400">+</span>
								<div
									className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
									ticket.powerBall
										? 'bg-gradient-to-br from-red-500 to-red-700 text-white'
										: 'bg-red-100 dark:bg-red-900/20 text-red-400 dark:text-red-600'
									}`}
								>
									{ticket.powerBall || '-'}
								</div>
								</div>
							</div>
							))}
						</div>
					</div>
				)}

				{/* Mega Millions Tickets */}
				{megaMillionsTickets.length > 0 && (
					<div>
						<h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
							<span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm">{LOTTO_TYPE.megamillions}</span>
							<span className="text-sm text-gray-600 dark:text-gray-400">({megaMillionsTickets.length} ใบ)</span>
						</h4>
						<h3 className="my-3 text-gray-900 dark:text-white">
							งวด{lot_mg_date ? formatDateFLot(parseDatetimeLocal(lot_mg_date)) : '..'}
						</h3>
						<div className="space-y-3">
							{megaMillionsTickets.map((ticket, index) => (
							<div key={ticket.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
								<div className="flex items-center justify-between mb-2">
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">ใบที่ {index + 1}</span>
								<span className="text-sm text-yellow-600 dark:text-yellow-400 font-semibold">{pricePerTicketMG} บาท</span>
								</div>
								<div className="flex flex-wrap items-center gap-2">
								{ticket.whiteBalls.map((num, ballIndex) => (
									<div
									key={ballIndex}
									className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
										num
										? 'bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-300 dark:to-blue-400 text-blue-900'
										: 'bg-blue-100 dark:bg-blue-800 text-blue-400 dark:text-blue-600'
									}`}
									>
									{num || '-'}
									</div>
								))}
								<span className="text-xl font-bold text-gray-600 dark:text-gray-400">+</span>
								<div
									className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
									ticket.powerBall
										? 'bg-gradient-to-br from-yellow-500 to-yellow-700 text-white'
										: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-400 dark:text-yellow-600'
									}`}
								>
									{ticket.powerBall || '-'}
								</div>
								</div>
							</div>
							))}
						</div>
					</div>
				)}

				{/* Total Summary */}
				<div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-4">
					<div className="flex justify-between items-center">
						<div>
							<p className="text-sm text-gray-600 dark:text-gray-400">จำนวนใบรวม</p>
							<p className="text-xl font-bold text-gray-900 dark:text-white">
							{allTickets.length} ใบ
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							PB: {powerballTickets.length} | MG: {megaMillionsTickets.length}
							</p>
						</div>
						<div className="text-right">
							<p className="text-sm text-gray-600 dark:text-gray-400">ราคารวมทั้งหมด</p>
							{discountInfo.totalDiscountAmount > 0 && (
								<p className="text-sm text-gray-500 dark:text-gray-400 line-through">
									{((powerballTickets.length * pricePerTicketPB) + (megaMillionsTickets.length * pricePerTicketMG)).toLocaleString()} บาท
								</p>
							)}
							<p className="text-3xl font-bold text-green-600 dark:text-green-400">
							{totalPrice.toLocaleString()}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">บาท</p>
							{discountInfo.totalDiscountAmount > 0 && (
								<p className="text-xs text-red-600 dark:text-red-400 font-semibold mt-1">
									ประหยัด {discountInfo.totalDiscountAmount.toLocaleString()} บาท
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Discount Options */}
				<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
					<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">โปรโมชั่นและส่วนลด</h4>
					<div className="space-y-3">
						{/* ส่วนลด Powerball ลูกค้าใหม่ */}
						{powerballTickets.length > 0 && discount_pb_new_customer_status === 0 && (
							<label className="flex items-start gap-3 cursor-pointer">
								<input
									type="checkbox"
									checked={useDiscountPB}
									onChange={(e) => setUseDiscountPB(e.target.checked)}
									className="mt-1 w-4 h-4 text-blue-600"
									disabled={powerballTickets.length < (customer_pb_new_count + customer_pb_new_free)}
								/>
								<div className="flex-1">
									<div className="font-medium text-gray-900 dark:text-white">
										ส่วนลดลูกค้าใหม่ {LOTTO_TYPE.powerball}
									</div>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										ซื้อ {customer_pb_new_count} ใบ ได้ฟรี {customer_pb_new_free} ใบ
										{powerballTickets.length < (customer_pb_new_count + customer_pb_new_free) && (
											<span className="text-red-500 ml-2">
												(ต้องซื้ออย่างน้อย {customer_pb_new_count + customer_pb_new_free} ใบ)
											</span>
										)}
									</div>
									{useDiscountPB && discountInfo.pbFreeTickets > 0 && (
										<div className="text-sm text-green-600 dark:text-green-400 font-semibold mt-1">
											ได้ฟรี {discountInfo.pbFreeTickets} ใบ (ประหยัด {discountInfo.pbDiscountAmount.toLocaleString()} บาท)
										</div>
									)}
								</div>
							</label>
						)}

						{/* ส่วนลด Mega Millions ลูกค้าใหม่ */}
						{megaMillionsTickets.length > 0 && discount_mg_new_customer_status === 0 && (
							<label className="flex items-start gap-3 cursor-pointer">
								<input
									type="checkbox"
									checked={useDiscountMG}
									onChange={(e) => setUseDiscountMG(e.target.checked)}
									className="mt-1 w-4 h-4 text-blue-600"
									disabled={megaMillionsTickets.length < (customer_mg_new_count + customer_mg_new_free)}
								/>
								<div className="flex-1">
									<div className="font-medium text-gray-900 dark:text-white">
										ส่วนลดลูกค้าใหม่ {LOTTO_TYPE.megamillions}
									</div>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										ซื้อ {customer_mg_new_count} ใบ ได้ฟรี {customer_mg_new_free} ใบ
										{megaMillionsTickets.length < (customer_mg_new_count + customer_mg_new_free) && (
											<span className="text-red-500 ml-2">
												(ต้องซื้ออย่างน้อย {customer_mg_new_count + customer_mg_new_free} ใบ)
											</span>
										)}
									</div>
									{useDiscountMG && discountInfo.mgFreeTickets > 0 && (
										<div className="text-sm text-green-600 dark:text-green-400 font-semibold mt-1">
											ได้ฟรี {discountInfo.mgFreeTickets} ใบ (ประหยัด {discountInfo.mgDiscountAmount.toLocaleString()} บาท)
										</div>
									)}
								</div>
							</label>
						)}

						{/* ส่วนลด Powerball ลูกค้าเก่า (ตามยอดสะสม) */}
						{powerballTickets.length > 0 && discount_pb_new_customer_status === 1 && customer_pb_old_count > 0 && (
							<label className="flex items-start gap-3 cursor-pointer">
								<input
									type="checkbox"
									checked={useDiscountPBOld}
									onChange={(e) => setUseDiscountPBOld(e.target.checked)}
									className="mt-1 w-4 h-4 text-blue-600"
									disabled={pbOldDiscountProgress.totalForCurrentPurchase < pbOldDiscountProgress.threshold}
								/>
								<div className="flex-1">
									<div className="font-medium text-gray-900 dark:text-white">
										ส่วนลดตามยอดสะสม {LOTTO_TYPE.powerball}
									</div>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										ซื้อ {customer_pb_old_count} ใบ ได้ฟรี {customer_pb_old_free} ใบ
										<div className="text-xs mt-1 space-y-0.5">
											<div>ยอดสะสมเก่าที่ยืนยันแล้ว: <span className="font-semibold">{discount_pb_order_number}</span> ใบ</div>
											<div>ใบในคำสั่งซื้อนี้: <span className="font-semibold">{powerballTickets.length}</span> ใบ <span className="text-gray-400 dark:text-gray-500">(จะเป็นยอดสะสมหลังอนุมัติ)</span></div>
											<div className="font-semibold text-blue-600 dark:text-blue-400">
												ยอดรวมสำหรับใช้สิทธิ์รอบนี้: {pbOldDiscountProgress.totalForCurrentPurchase} ใบ
												{pbOldDiscountProgress.totalSets > 0 ? ` (ได้ ${pbOldDiscountProgress.totalSets} ใบ)` : ''}
											</div>
										</div>
										{pbOldDiscountProgress.totalSets === 0 ? <span className="text-red-500 ml-2">
												{/* (ยอดสะสมต้องรวมกันได้ {customer_pb_old_count + customer_pb_old_free} ใบ ขึ้นไป) */}
											</span> : null}
									</div>
									{useDiscountPBOld && discountInfo.pbFreeTickets > 0 && (
										<div className="text-sm text-green-600 dark:text-green-400 font-semibold mt-1">
											ได้ฟรี {discountInfo.pbFreeTickets} ใบ (ประหยัด {discountInfo.pbDiscountAmount.toLocaleString()} บาท)
										</div>
									)}
								</div>
							</label>
						)}

						{/* ส่วนลด Mega Millions ลูกค้าเก่า (ตามยอดสะสม) */}
						{megaMillionsTickets.length > 0 && discount_mg_new_customer_status === 1 && customer_mg_old_count > 0 && (
							<label className="flex items-start gap-3 cursor-pointer">
								<input
									type="checkbox"
									checked={useDiscountMGOld}
									onChange={(e) => setUseDiscountMGOld(e.target.checked)}
									className="mt-1 w-4 h-4 text-blue-600"
									disabled={mgOldDiscountProgress.totalForCurrentPurchase < mgOldDiscountProgress.threshold}
								/>
								<div className="flex-1">
									<div className="font-medium text-gray-900 dark:text-white">
										ส่วนลดตามยอดสะสม {LOTTO_TYPE.megamillions}
									</div>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										ซื้อ {customer_mg_old_count} ใบ ได้ฟรี {customer_mg_old_free} ใบ
										<div className="text-xs mt-1 space-y-0.5">
											<div>ยอดสะสมเก่าที่ยืนยันแล้ว: <span className="font-semibold">{discount_mg_order_number}</span> ใบ</div>
											<div>ใบในคำสั่งซื้อนี้: <span className="font-semibold">{megaMillionsTickets.length}</span> ใบ <span className="text-gray-400 dark:text-gray-500">(จะเป็นยอดสะสมหลังอนุมัติ)</span></div>
											<div className="font-semibold text-blue-600 dark:text-blue-400">
												ยอดรวมสำหรับใช้สิทธิ์รอบนี้: {mgOldDiscountProgress.totalForCurrentPurchase} ใบ
												{mgOldDiscountProgress.totalSets > 0 ? ` (ได้ ${mgOldDiscountProgress.totalSets} ใบ)` : ''}
											</div>
										</div>
										{mgOldDiscountProgress.totalSets === 0 ? <span className="text-red-500 ml-2">
												{/* (ยอดสะสมต้องรวมกันได้ {customer_mg_old_count + customer_mg_old_free} ใบ ขึ้นไป) */}
											</span> : null}
									</div>
									{useDiscountMGOld && discountInfo.mgFreeTickets > 0 && (
										<div className="text-sm text-green-600 dark:text-green-400 font-semibold mt-1">
											ได้ฟรี {discountInfo.mgFreeTickets} ใบ (ประหยัด {discountInfo.mgDiscountAmount.toLocaleString()} บาท)
										</div>
									)}
								</div>
							</label>
						)}

						{/* ถ้าไม่มีส่วนลดที่ใช้ได้ */}
						{(!powerballTickets.length || (discount_pb_new_customer_status === 1 && customer_pb_old_count === 0)) &&
						 (!megaMillionsTickets.length || (discount_mg_new_customer_status === 1 && customer_mg_old_count === 0)) && (
							<div className="text-sm text-gray-500 dark:text-gray-400 italic">
								ไม่มีโปรโมชั่นที่ใช้ได้ในขณะนี้
							</div>
						)}
					</div>
				</div>

				{/* Payment Method Selection */}
				<div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
					<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">วิธีชำระเงิน</h4>
					<div className="space-y-3">
						<div className="flex items-start gap-3">
							<div className="flex-1">
								<div className="font-medium text-gray-900 dark:text-white">สแกนจ่าย</div>
								<div className="text-sm text-gray-500 dark:text-gray-400">อัพโหลดสลิปการชำระเงินและรอการอนุมัติ</div>
							</div>
						</div>
					</div>
				</div>

				{/* Warning Note */}
				<div className="mt-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
					<div className="flex gap-2">
					<svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
						<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
					</svg>
					<div className="text-xs text-yellow-800 dark:text-yellow-200">
						<p className="font-semibold">หมายเหตุ:</p>
						<ul className="mt-1 space-y-1 list-disc list-inside">
							<li>หลังจากกด "ดำเนินการชำระเงิน" โอนเงินตามยอดที่ระบุ ภายใน {PAYMENT_TIME_MINUTES} นาที</li>
							<li>หากเกิน {PAYMENT_TIME_MINUTES} นาที คำสั่งซื้อจะถูกยกเลิก</li>
							<li>คำสั่งซื้อจะสมบูรณ์เมื่อผู้ดูแลอนุมัติ</li>
							<li>กรณีมีการใช้ส่วนลด หากคำสั่งซื้อไม่สมบูรณ์ ส่วนลดจะถูกคืน</li>
							<li>หากมีข้อสงสัย กรุณาติดต่อผู้แอดมิน</li>
							<li>หากออกจากหน้าจอชำระเงินแล้ว สามารถกลับมาชำระอีกครั้งได้ภายใน {PAYMENT_TIME_MINUTES} นาที ที่หน้า "ประวัติการสั่งซื้อ"</li>
						</ul>
					</div>
					</div>
				</div>

				<div className="flex justify-end gap-3 pt-4">
					<button
						type="button"
						onClick={onClose}
						className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition font-semibold"
					>
						ยกเลิก
					</button>
					<button
						type="button"
						onClick={onConfirm}
						className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition font-semibold shadow-lg"
					>
						ดำเนินการชำระเงิน
					</button>
				</div>
			</div>
		</div>
    </div>
  );
};

// Payment Modal Component (QR Code & Slip Upload)
const PaymentModal = ({
  totalPrice,
  pay_bank_name,
  pay_bank_account,
  pay_bank_account_name,
  pay_prompt_pay,
  pay_prompt_pay_name,
  slipFile,
  slipPreview,
  handleFileChange,
  handleRemoveSlip,
  onConfirm,
  onClose,
  onCloseAll,
  resetAll
}) => {
  const [purchaseTime, setPurchaseTime] = useState(null);
  const [countdownMs, setCountdownMs] = useState(null);

  const formatTimeLeft = (ms) => {
    if (ms == null) return '';
    if (ms <= 0) return 'หมดเวลาแล้ว';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} นาที ${seconds} วินาที`;
  };

  useEffect(() => {
    const now = new Date();
    setPurchaseTime(now);
    const deadline = new Date(now.getTime() + PAYMENT_TIME_MINUTES * 60 * 1000);
    const update = () => {
      const diff = deadline - new Date();
      setCountdownMs(diff > 0 ? diff : 0);
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, []);

  // Close all modals and notify user when countdown expires
  useEffect(() => {
    if (countdownMs === 0) {
      if (typeof onCloseAll === 'function') {
        onCloseAll();
      } else if (typeof onClose === 'function') {
        onClose();
      }
	  resetAll();
      showError('หมดเวลาชำระเงิน', 'เวลาชำระเงินของคำสั่งซื้อนี้หมดแล้ว หากมีข้อสงสัยหรือต้องการความช่วยเหลือกรุณาติดต่อแอดมิน');
    }
  }, [countdownMs, onCloseAll, onClose, resetAll]);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg my-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">ชำระเงิน</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="ปิด"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* QR Code and Account Details */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              ช่องทางการชำระเงิน
            </h4>

            <div className="grid md:grid-cols-1 gap-4 mb-3">
              <div className="flex flex-col items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <p className="text-base font-semibold text-gray-900 dark:text-white">จ่ายผ่าน พร้อมเพย์</p>
                <div className="bg-white rounded-lg p-2">
                  <img
                    src={generatePromptPayQRCodeURL(pay_prompt_pay, totalPrice)}
                    alt="PromptPay QR Code"
                    className="w-40 h-40"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f3f4f6'/%3E%3Ctext x='100' y='100' text-anchor='middle' fill='%236b7280' font-size='14'%3EQR Code%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
                {pay_prompt_pay_name && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">ชื่อบัญชี: {pay_prompt_pay_name}</p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">เลขพร้อมเพย์: {pay_prompt_pay}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-1 gap-4 mb-3">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">จ่ายผ่าน บัญชีธนาคาร</p>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <p><span className="font-medium">ธนาคาร:</span> {pay_bank_name}</p>
                  <p><span className="font-medium">เลขบัญชี:</span> {pay_bank_account}</p>
                  <p><span className="font-medium">ชื่อบัญชี:</span> {pay_bank_account_name}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-1 gap-4 mb-3">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">ยอดที่ต้องชำระ</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-2"> {totalPrice.toLocaleString()} บาท</p>
              </div>
            </div>

            <div className="grid md:grid-cols-1 gap-4">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">กรุณาชำระเงินภายใน {PAYMENT_TIME_MINUTES} นาที</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">สั่งซื้อเมื่อ: {purchaseTime ? formatDateTimeTable(purchaseTime) : '-'}</p>
                <p className="text-xl font-semibold text-red-600 dark:text-red-400">{countdownMs == null ? 'กำลังโหลด...' : (countdownMs > 0 ? formatTimeLeft(countdownMs) : 'หมดเวลาแล้ว')}</p>
              </div>
            </div>

            <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="text-xs text-yellow-800 dark:text-yellow-200">
					<p className="font-semibold">หมายเหตุ:</p>
					<ul className="mt-1 space-y-1 list-disc list-inside">
							<li>กรุณาโอนเงินตามยอดที่ระบุ ภายใน {PAYMENT_TIME_MINUTES} นาที</li>
							<li>หากเกิน {PAYMENT_TIME_MINUTES} นาที คำสั่งซื้อจะถูกยกเลิก</li>
							<li>อัพโหลดหลักฐานการชำระเงินด้านล่าง</li>
							<li>คำสั่งซื้อจะสมบูรณ์เมื่อผู้ดูแลอนุมัติ</li>
							<li>หากมีข้อสงสัย กรุณาติดต่อผู้แอดมิน</li>
							<li>หากออกจากหน้าจอชำระเงินแล้ว สามารถกลับมาชำระอีกครั้งได้ภายใน {PAYMENT_TIME_MINUTES} นาที ที่หน้า "ประวัติการสั่งซื้อ"</li>
					</ul>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">อัพโหลดหลักฐานการชำระเงิน</h4>
            {!slipPreview ? (
              <div>
                <label className="block w-full cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">คลิกเพื่อเลือกไฟล์</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">รองรับไฟล์รูปภาพ ขนาดไม่เกิน 5MB</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="relative">
                <img src={slipPreview} alt="Payment Slip" className="w-full max-h-64 object-contain rounded-lg border border-gray-300 dark:border-gray-600" />
                <button
                  type="button"
                  onClick={handleRemoveSlip}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition font-semibold"
            >
              ย้อนกลับ
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!slipFile || countdownMs === 0}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition font-semibold shadow-lg"
            >
              ยืนยันชำระเงิน
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Number Selection Modal Component
const NumberSelectionModal = ({ ticket, ticketType, onUpdate, onClose }) => {
  const [whiteBalls, setWhiteBalls] = useState([...ticket.whiteBalls]);
  const [powerBall, setPowerBall] = useState(ticket.powerBall);

  // กำหนดช่วงเลขตามประเภท
  // Powerball: white 1-69, red 1-26
  // Mega Millions: white 1-75, red 1-46
  const maxWhite = ticketType === 'pb' ? 69 : 70;
  const maxPower = ticketType === 'pb' ? 26 : 24;
  const powerBallLabel = ticketType === 'pb' ? 'Powerball' : 'Mega Ball';
  const colorBallGrey = ticketType === 'pb' ? 'gray' : 'blue';
  const colorBallSpecial = ticketType === 'pb' ? 'red' : 'yellow';
  const handleWhiteBallSelect = (num) => {
    const numStr = String(num);
    // ตรวจสอบว่าเลขนี้ถูกเลือกไว้แล้วหรือไม่
    const existingIndex = whiteBalls.findIndex(ball => ball === numStr);

    if (existingIndex !== -1) {
      // ถ้าเลือกไว้แล้ว ให้ลบออก
      const newWhiteBalls = [...whiteBalls];
      newWhiteBalls[existingIndex] = '';
      setWhiteBalls(newWhiteBalls);
    } else {
      // ถ้ายังไม่เลือก ให้เพิ่มเข้าไปในช่องว่างแรก
      const emptyIndex = whiteBalls.findIndex(ball => ball === '');
      if (emptyIndex !== -1) {
        const newWhiteBalls = [...whiteBalls];
        newWhiteBalls[emptyIndex] = numStr;
        setWhiteBalls(newWhiteBalls);
      }
    }
  };

  const handleWhiteBallInput = (index, value) => {
    if (value === '') {
      const newWhiteBalls = [...whiteBalls];
      newWhiteBalls[index] = '';
      setWhiteBalls(newWhiteBalls);
      return;
    }

    const num = parseInt(value);
    if (!isNaN(num) && num >= 1 && num <= maxWhite) {
      // ตรวจสอบว่าไม่ซ้ำกับลูกอื่น
      const isDuplicate = whiteBalls.some((ball, idx) => idx !== index && ball === String(num));
      if (!isDuplicate) {
        const newWhiteBalls = [...whiteBalls];
        newWhiteBalls[index] = String(num);
        setWhiteBalls(newWhiteBalls);
      }
    }
  };

  const handlePowerBallSelect = (num) => {
    const numStr = String(num);
    // ถ้ากดเลขเดิมที่เลือกไว้แล้ว ให้ลบออก
    if (powerBall === numStr) {
      setPowerBall('');
    } else {
      setPowerBall(numStr);
    }
  };

  const handlePowerBallInput = (value) => {
    if (value === '') {
      setPowerBall('');
      return;
    }

    const num = parseInt(value);
    if (!isNaN(num) && num >= 1 && num <= maxPower) {
      setPowerBall(String(num));
    }
  };

  const handleConfirm = () => {
    onUpdate(whiteBalls, powerBall);
    onClose();
  };

  return (
    <div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 h-screen max-h-screen overflow-y-auto flex align-middle">
      <div className="flex align-middle h-auto my-auto">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-auto w-full max-w-lg md:min-w-[600px] </div>my-5">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">เลือกเลข</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="ปิด"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Selected Numbers Display with Input */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">เลขที่เลือก (พิมพ์เลขได้โดยตรง)</h4>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-2">
                  {whiteBalls.map((num, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <input
                        type="number"
                        min="1"
                        max={maxWhite}
                        value={num}
                        onChange={(e) => handleWhiteBallInput(index, e.target.value)}
                        placeholder="-"
                        className={`w-12 h-12 text-center rounded-full font-bold text-lg border-2 ${
                          num
                            ? `bg-gradient-to-br from-${colorBallGrey}-200 to-${colorBallGrey}-300 dark:from-${colorBallGrey}-300 dark:to-${colorBallGrey}-400 border-${colorBallGrey}-400 text-${colorBallGrey}-900`
                            : `bg-${colorBallGrey}-100 dark:bg-${colorBallGrey}-800 border-${colorBallGrey}-300 dark:border-${colorBallGrey}-600 text-${colorBallGrey}-400 dark:text-${colorBallGrey}-600`
                        } focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                      />
                      <span className={`text-xs text-${colorBallGrey}-500 mt-1`}>1-{maxWhite}</span>
                    </div>
                  ))}
                </div>
                <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">+</span>
                <div className="flex flex-col items-center">
                  <input
                    type="number"
                    min="1"
                    max={maxPower}
                    value={powerBall}
                    onChange={(e) => handlePowerBallInput(e.target.value)}
                    placeholder="-"
                    className={`w-12 h-12 text-center rounded-full font-bold text-lg border-2 ${
                      powerBall
                        ? `bg-gradient-to-br from-${colorBallSpecial}-500 to-${colorBallSpecial}-700 border-${colorBallSpecial}-600 text-white shadow-lg shadow-${colorBallSpecial}-500/30`
                        : `bg-${colorBallSpecial}-100 dark:bg-${colorBallSpecial}-900/20 border-${colorBallSpecial}-300 dark:border-${colorBallSpecial}-700 text-${colorBallSpecial}-400 dark:text-${colorBallSpecial}-600`
                    } focus:ring-2 focus:ring-${colorBallSpecial}-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  />
                  <span className={`text-xs text-${colorBallSpecial}-500 mt-1`}>1-{maxPower}</span>
                </div>
              </div>
            </div>

            {/* White Ball Selection */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">เลือกบอล{ticketType === 'pb' ? 'ขาว' : 'ฟ้า'} (1-{maxWhite})</h4>
              <div className="grid grid-cols-8 md:grid-cols-10 gap-2 p-2">
                {Array.from({ length: maxWhite }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handleWhiteBallSelect(n)}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${
                      whiteBalls.includes(String(n))
                        ? `bg-${colorBallGrey}-500 text-white shadow-md`
                        : `bg-${colorBallGrey}-100 dark:bg-${colorBallGrey}-700 text-${colorBallGrey}-900 dark:text-white hover:bg-${colorBallGrey}-100 dark:hover:bg-${colorBallGrey}-600 hover:scale-110`
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Powerball Selection */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">เลือกบอล{ticketType === 'pb' ? 'แดง' : 'เหลือง'} ({powerBallLabel} 1-{maxPower})</h4>
              <div className="grid grid-cols-8 md:grid-cols-10 gap-2">
                {Array.from({ length: maxPower }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handlePowerBallSelect(n)}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${
                      powerBall === String(n)
                        ? `bg-${colorBallSpecial}-500 text-white shadow-lg shadow-${colorBallSpecial}-500/50`
                        : `bg-${colorBallSpecial}-100 dark:bg-${colorBallSpecial}-900/20 text-${colorBallSpecial}-600 dark:text-${colorBallSpecial}-400 hover:bg-${colorBallSpecial}-200 dark:hover:bg-${colorBallSpecial}-900/40 hover:scale-110`
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleConfirm}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-colors"
              >
                เสร็จสิ้น
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyPowerball;
